/**
 * Stock-audit drill-downs — one dialog, four bodies.
 *
 * Every table on `/inventory/audits` ended at a summary number with no way to
 * ask "which lines?", so this is the answer to a row click:
 *   - session       one audit's line items, with the signed variance per line
 *   - sku           one product's audit history, its trend, and who counted it
 *   - packer        one packer's sessions, worst SKUs, and lost-item reports
 *   - coverageStore audited vs never-audited SKUs for a store, by stock value
 *
 * Mounted once via {@link AuditDrilldownDialogRoot} inside `AppShell`.
 */
import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ScanLine } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { BarChart } from '@/components/charts/BarChart';
import { rangeISO, useFilters } from '@/state/filters';
import {
  auditDrilldownKey,
  closeAuditDrilldown,
  useAuditDrilldownStore,
  type AuditDrilldownTarget,
} from '@/state/auditDrilldownDialog';
import {
  useAuditsCoverageStore,
  useAuditsPackerDetail,
  useAuditsSessions,
  useAuditsSkuDetail,
} from '@/api/bi/audits';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

/** Signed variance: negative is missing stock, positive is an overcount. */
function VarianceBadge({ value }: { value: number }) {
  if (value === 0) return <Badge tone="success">match</Badge>;
  return (
    <Badge tone={value < 0 ? 'danger' : 'warning'}>
      {value > 0 ? `+${value}` : value}
    </Badge>
  );
}

function Header({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: ReactNode;
}) {
  return (
    <header className="border-b border-border px-5 py-4">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {eyebrow}
      </div>
      <h2 className="truncate text-sm font-semibold">{title}</h2>
      {subtitle ? (
        <p className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</p>
      ) : null}
    </header>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium">{value}</div>
    </div>
  );
}

function Scroll({ children }: { children: ReactNode }) {
  return <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>;
}

function Loading() {
  return (
    <div className="space-y-3 p-5">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

function Table({
  head,
  children,
  maxHeight = 'max-h-72',
}: {
  head: ReactNode;
  children: ReactNode;
  maxHeight?: string;
}) {
  return (
    <div className={cn('overflow-auto rounded-md border border-border', maxHeight)}>
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-muted/60 text-[10px] uppercase text-muted-foreground">
          {head}
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

function SessionBody({ auditId }: { auditId: number }) {
  // The sessions endpoint already returns `items[]` when `audit_id` is passed,
  // so this drill-down needed no new backend surface. Pagination is left at
  // defaults; one session's line count is well inside a page.
  const query = useAuditsSessions({ auditId, pageSize: 100 });

  if (query.isLoading) return <Loading />;
  if (query.isError || !query.data) {
    return (
      <div className="p-5">
        <ErrorState
          description="Could not load this audit session."
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const session = query.data.results.find((r) => r.id === auditId) ?? null;
  const items = query.data.items;
  const matched = items.filter((i) => i.signed_variance === 0).length;

  return (
    <>
      <Header
        eyebrow="Audit session"
        title={`Audit #${auditId}`}
        subtitle={
          session
            ? [
                session.store_name,
                session.packer_name,
                session.created_at ? `started ${fmt.datetime(session.created_at)}` : null,
                session.completed_at
                  ? `completed ${fmt.datetime(session.completed_at)}`
                  : 'not completed',
                session.wrapped_around ? 'rotation wrapped' : null,
              ]
                .filter(Boolean)
                .join(' · ')
            : undefined
        }
      />
      <Scroll>
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Lines" value={fmt.number(items.length)} />
          <Stat label="Matched" value={pct(items.length ? matched / items.length : null)} />
          <Stat
            label="Shortfall units"
            value={fmt.number(session?.shortfall_units ?? 0)}
          />
          <Stat
            label="Overcount units"
            value={fmt.number(session?.overcount_units ?? 0)}
          />
        </div>

        {items.length === 0 ? (
          <EmptyState
            icon={<ScanLine className="h-5 w-5" />}
            title="No line items"
            description="This session has no recorded audit lines."
          />
        ) : (
          <Table
            maxHeight="max-h-[52vh]"
            head={
              <tr>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-left">Bucket</th>
                <th className="px-3 py-2 text-right">Expected</th>
                <th className="px-3 py-2 text-right">Scanned</th>
                <th className="px-3 py-2 text-right">Variance</th>
                <th className="px-3 py-2 text-left">Done</th>
              </tr>
            }
          >
            {items.map((it) => (
              <tr key={it.id} className="border-t border-border">
                <td className="px-3 py-2">{it.product_name}</td>
                <td className="px-3 py-2 text-muted-foreground">{it.audit_type}</td>
                <td className="px-3 py-2 text-right">{it.expected_quantity}</td>
                <td className="px-3 py-2 text-right">{it.scanned_quantity}</td>
                <td className="px-3 py-2 text-right">
                  <VarianceBadge value={it.signed_variance} />
                </td>
                <td className="px-3 py-2">{it.is_completed ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Scroll>
    </>
  );
}

// ---------------------------------------------------------------------------
// SKU
// ---------------------------------------------------------------------------

function SkuBody({
  productId,
  productName,
  storeId,
  start,
  end,
}: {
  productId: number;
  productName?: string | null;
  storeId?: number | null;
  start: string;
  end: string;
}) {
  const query = useAuditsSkuDetail({ productId, storeId, start, end });

  if (query.isLoading) return <Loading />;
  if (query.isError || !query.data) {
    return (
      <div className="p-5">
        <ErrorState
          description="Could not load this SKU's audit history."
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const d = query.data;
  const t = d.totals;

  return (
    <>
      <Header
        eyebrow="SKU audit history"
        title={d.product.name ?? productName ?? `Product #${productId}`}
        subtitle={`${fmt.number(t.audits)} audits · ${fmt.number(t.lines)} lines${
          storeId ? '' : ' · all stores in your scope'
        }`}
      />
      <Scroll>
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Match rate" value={pct(t.match_rate)} />
          <Stat label="Shortfall units" value={fmt.number(t.shortfall_units)} />
          <Stat label="Overcount units" value={fmt.number(t.overcount_units)} />
          <Stat
            label="Expected / scanned"
            value={`${fmt.number(t.expected_units)} / ${fmt.number(t.scanned_units)}`}
          />
        </div>

        <div className="mb-4 text-xs">
          <Link
            to={`/inventory/lost-items?product_id=${productId}&approval_status=all`}
            className="text-primary hover:underline"
          >
            Lost-item reports for this SKU
          </Link>
        </div>

        {d.by_day.length > 0 ? (
          <section className="mb-4">
            <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              Shortfall units by day
            </h3>
            <BarChart
              data={d.by_day.map((r) => ({ name: r.day ?? '—', value: r.shortfall_units }))}
              xKey="name"
              yKey="value"
              height={180}
            />
          </section>
        ) : null}

        {d.by_packer.length > 0 ? (
          <section className="mb-4">
            <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              Who counted it
            </h3>
            <Table
              head={
                <tr>
                  <th className="px-3 py-2 text-left">Packer</th>
                  <th className="px-3 py-2 text-right">Lines</th>
                  <th className="px-3 py-2 text-right">Shortfall</th>
                  <th className="px-3 py-2 text-right">Overcount</th>
                </tr>
              }
            >
              {d.by_packer.map((r) => (
                <tr key={r.packer_id ?? r.packer_name} className="border-t border-border">
                  <td className="px-3 py-2">{r.packer_name}</td>
                  <td className="px-3 py-2 text-right">{r.lines}</td>
                  <td className="px-3 py-2 text-right">{r.shortfall_units}</td>
                  <td className="px-3 py-2 text-right">{r.overcount_units}</td>
                </tr>
              ))}
            </Table>
          </section>
        ) : null}

        <section>
          <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            Every audit line ({d.lines.length})
          </h3>
          {d.lines.length === 0 ? (
            <EmptyState
              icon={<ScanLine className="h-5 w-5" />}
              title="Never audited in this window"
              description="This SKU has no audit lines for the selected range."
            />
          ) : (
            <Table
              head={
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Store</th>
                  <th className="px-3 py-2 text-left">Packer</th>
                  <th className="px-3 py-2 text-right">Expected</th>
                  <th className="px-3 py-2 text-right">Scanned</th>
                  <th className="px-3 py-2 text-right">Variance</th>
                </tr>
              }
            >
              {d.lines.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                    {fmt.datetime(r.created_at)}
                  </td>
                  <td className="px-3 py-2">{r.store_name ?? '—'}</td>
                  <td className="px-3 py-2">{r.packer_name ?? '—'}</td>
                  <td className="px-3 py-2 text-right">{r.expected_quantity}</td>
                  <td className="px-3 py-2 text-right">{r.scanned_quantity}</td>
                  <td className="px-3 py-2 text-right">
                    <VarianceBadge value={r.signed_variance} />
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </section>
      </Scroll>
    </>
  );
}

// ---------------------------------------------------------------------------
// Packer
// ---------------------------------------------------------------------------

function PackerBody({
  packerId,
  packerName,
  start,
  end,
}: {
  packerId: number;
  packerName?: string | null;
  start: string;
  end: string;
}) {
  const query = useAuditsPackerDetail({ packerId, start, end });

  if (query.isLoading) return <Loading />;
  if (query.isError || !query.data) {
    return (
      <div className="p-5">
        <ErrorState
          description="Could not load this packer's audit record."
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const d = query.data;
  const t = d.totals;
  const lost = d.lost_items;

  return (
    <>
      <Header
        eyebrow="Packer audit record"
        title={d.packer.name || packerName || `Packer #${packerId}`}
        subtitle={`${fmt.number(t.sessions)} sessions · ${fmt.number(t.lines)} lines · ${fmt.number(t.skus)} SKUs`}
      />
      <Scroll>
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Match rate" value={pct(t.match_rate)} />
          <Stat label="Shortfall units" value={fmt.number(t.shortfall_units)} />
          <Stat label="Overcount units" value={fmt.number(t.overcount_units)} />
          <Stat
            label="Lost reports filed"
            value={lost ? fmt.number(lost.reports) : '—'}
          />
        </div>

        {lost ? (
          <p className="mb-4 rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
            Filed {fmt.number(lost.reports)} lost-item reports ({fmt.number(lost.units)}{' '}
            units) in this window — {lost.approved} approved, {lost.rejected} rejected,{' '}
            {lost.pending} pending. A low match rate alongside a high report count is
            the combination worth investigating; either number alone usually just
            reflects a busy shift.{' '}
            <Link
              to={`/inventory/lost-items?reported_by=${lost.user_id}&approval_status=all`}
              className="text-primary hover:underline"
            >
              View their reports
            </Link>
          </p>
        ) : null}

        {d.by_day.length > 0 ? (
          <section className="mb-4">
            <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              Match rate by day
            </h3>
            <BarChart
              data={d.by_day.map((r) => ({
                name: r.day ?? '—',
                value: Number((r.match_rate * 100).toFixed(1)),
              }))}
              xKey="name"
              yKey="value"
              height={180}
              formatValue={(v) => `${v}%`}
            />
          </section>
        ) : null}

        {d.worst_skus.length > 0 ? (
          <section className="mb-4">
            <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              Worst SKUs
            </h3>
            <Table
              head={
                <tr>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Lines</th>
                  <th className="px-3 py-2 text-right">Shortfall</th>
                  <th className="px-3 py-2 text-right">Overcount</th>
                </tr>
              }
            >
              {d.worst_skus.map((r) => (
                <tr key={r.product_id} className="border-t border-border">
                  <td className="px-3 py-2">{r.product_name}</td>
                  <td className="px-3 py-2 text-right">{r.lines}</td>
                  <td className="px-3 py-2 text-right">{r.shortfall_units}</td>
                  <td className="px-3 py-2 text-right">{r.overcount_units}</td>
                </tr>
              ))}
            </Table>
          </section>
        ) : null}

        <section>
          <h3 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            Sessions ({d.sessions.length})
          </h3>
          {d.sessions.length === 0 ? (
            <EmptyState
              icon={<ScanLine className="h-5 w-5" />}
              title="No sessions"
              description="This packer ran no audits in the selected range."
            />
          ) : (
            <Table
              head={
                <tr>
                  <th className="px-3 py-2 text-left">Audit</th>
                  <th className="px-3 py-2 text-left">Store</th>
                  <th className="px-3 py-2 text-left">Started</th>
                  <th className="px-3 py-2 text-right">Lines</th>
                  <th className="px-3 py-2 text-right">Match</th>
                  <th className="px-3 py-2 text-right">Shortfall</th>
                </tr>
              }
            >
              {d.sessions.map((r) => (
                <tr key={r.audit_id} className="border-t border-border">
                  <td className="px-3 py-2">#{r.audit_id}</td>
                  <td className="px-3 py-2">{r.store_name ?? '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                    {fmt.datetime(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-right">{r.item_count}</td>
                  <td className="px-3 py-2 text-right">{pct(r.match_rate)}</td>
                  <td className="px-3 py-2 text-right">{r.shortfall_units}</td>
                </tr>
              ))}
            </Table>
          )}
        </section>
      </Scroll>
    </>
  );
}

// ---------------------------------------------------------------------------
// Coverage by store
// ---------------------------------------------------------------------------

function CoverageStoreBody({
  storeId,
  storeName,
  start,
  end,
}: {
  storeId: number;
  storeName?: string | null;
  start: string;
  end: string;
}) {
  const query = useAuditsCoverageStore({ storeId, start, end });

  if (query.isLoading) return <Loading />;
  if (query.isError || !query.data) {
    return (
      <div className="p-5">
        <ErrorState
          description="Could not load coverage for this store."
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  const d = query.data;
  const t = d.totals;

  return (
    <>
      <Header
        eyebrow="Store audit coverage"
        title={d.store.name ?? storeName ?? `Store #${storeId}`}
        subtitle={
          d.rotation
            ? `Rotation round ${d.rotation.current_round}`
            : 'No rotation record'
        }
      />
      <Scroll>
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="SKUs in stock" value={fmt.number(t.skus_in_stock)} />
          <Stat label="Audited" value={fmt.number(t.skus_audited_in_stock)} />
          <Stat label="Never audited" value={fmt.number(t.skus_never_audited)} />
          <Stat label="Coverage" value={pct(t.coverage_rate)} />
        </div>

        <Tabs defaultValue="never">
          <TabsList className="mb-3">
            <TabsTrigger value="never">Never audited</TabsTrigger>
            <TabsTrigger value="audited">Audited</TabsTrigger>
          </TabsList>
          <TabsContent value="never">
            <p className="mb-2 text-[11px] text-muted-foreground">
              In-stock SKUs with no audit line in this window, ranked by stock value —
              an expensive blind spot matters more than a cheap high-count one.
            </p>
            {d.never_audited.length === 0 ? (
              <EmptyState
                icon={<ScanLine className="h-5 w-5" />}
                title="Full coverage"
                description="Every in-stock SKU was audited in this window."
              />
            ) : (
              <Table
                maxHeight="max-h-[44vh]"
                head={
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">In stock</th>
                    <th className="px-3 py-2 text-right">Stock value</th>
                  </tr>
                }
              >
                {d.never_audited.map((r) => (
                  <tr key={r.product_id} className="border-t border-border">
                    <td className="px-3 py-2">{r.product_name}</td>
                    <td className="px-3 py-2 text-right">{r.stock_quantity}</td>
                    <td className="px-3 py-2 text-right">
                      {r.stock_value != null ? fmt.npr(r.stock_value) : 'Unknown cost'}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </TabsContent>
          <TabsContent value="audited">
            {d.audited.length === 0 ? (
              <EmptyState
                icon={<ScanLine className="h-5 w-5" />}
                title="No audits"
                description="No SKU was audited at this store in the selected range."
              />
            ) : (
              <Table
                maxHeight="max-h-[44vh]"
                head={
                  <tr>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">Audits</th>
                    <th className="px-3 py-2 text-right">Shortfall</th>
                    <th className="px-3 py-2 text-right">Overcount</th>
                    <th className="px-3 py-2 text-left">Last audited</th>
                  </tr>
                }
              >
                {d.audited.map((r) => (
                  <tr key={r.product_id} className="border-t border-border">
                    <td className="px-3 py-2">{r.product_name}</td>
                    <td className="px-3 py-2 text-right">{r.audits}</td>
                    <td className="px-3 py-2 text-right">{r.shortfall_units}</td>
                    <td className="px-3 py-2 text-right">{r.overcount_units}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                      {fmt.datetime(r.last_audited)}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </TabsContent>
        </Tabs>
      </Scroll>
    </>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

function Body({ target }: { target: AuditDrilldownTarget }) {
  const { range, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);

  switch (target.kind) {
    case 'session':
      return <SessionBody auditId={target.auditId} />;
    case 'sku':
      return (
        <SkuBody
          productId={target.productId}
          productName={target.productName}
          storeId={target.storeId}
          start={iso.start}
          end={iso.end}
        />
      );
    case 'packer':
      return (
        <PackerBody
          packerId={target.packerId}
          packerName={target.packerName}
          start={iso.start}
          end={iso.end}
        />
      );
    case 'coverageStore':
      return (
        <CoverageStoreBody
          storeId={target.storeId}
          storeName={target.storeName}
          start={iso.start}
          end={iso.end}
        />
      );
  }
}

export function AuditDrilldownDialogRoot() {
  const open = useAuditDrilldownStore((s) => s.open);
  const target = useAuditDrilldownStore((s) => s.target);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeAuditDrilldown();
      }}
    >
      <DialogContent
        className={cn(
          'flex max-h-[min(90vh,900px)] w-[calc(100vw-1.5rem)] max-w-4xl flex-col overflow-hidden p-0',
          'gap-0 sm:max-w-4xl',
        )}
      >
        <DialogTitle className="sr-only">Stock audit detail</DialogTitle>
        {target ? <Body key={auditDrilldownKey(target)} target={target} /> : null}
      </DialogContent>
    </Dialog>
  );
}
